import {
  getAuth,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail as firebaseSendPasswordResetEmail,
  sendEmailVerification,
  signOut as firebaseSignOut,
  onAuthStateChanged as firebaseOnAuthStateChanged,
  verifyPasswordResetCode as firebaseVerifyPasswordResetCode,
  confirmPasswordReset as firebaseConfirmPasswordReset,
  GoogleAuthProvider,
  EmailAuthProvider,
  reauthenticateWithPopup,
  reauthenticateWithCredential,
  updateProfile,
  User,
} from 'firebase/auth';
import type { IAuthService, AuthUser, AuthUnsubscribe } from '@breath-lab/services';
import { firebaseApp } from '../firebase/firebaseConfig';

function toAuthUser(user: User | null): AuthUser | null {
  if (!user) return null;
  return {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName,
    emailVerified: user.emailVerified,
    providerData: user.providerData.map((p) => ({
      providerId: p.providerId,
      email: p.email,
    })),
  };
}

export class WebAuthService implements IAuthService {
  private auth = getAuth(firebaseApp);

  async signInWithGoogle(): Promise<AuthUser | null> {
    try {
      const credential = await signInWithPopup(this.auth, new GoogleAuthProvider());
      return toAuthUser(credential.user);
    } catch (err) {
      if ((err as { code?: string }).code === 'auth/web-storage-unsupported') {
        await signInWithRedirect(this.auth, new GoogleAuthProvider());
        return null;
      }
      throw err;
    }
  }

  async getGoogleRedirectResult(): Promise<AuthUser | null> {
    const credential = await getRedirectResult(this.auth);
    return credential ? toAuthUser(credential.user) : null;
  }

  async signInWithEmail(email: string, password: string): Promise<AuthUser> {
    const credential = await signInWithEmailAndPassword(this.auth, email, password);
    return toAuthUser(credential.user)!;
  }

  async registerWithEmail(email: string, password: string): Promise<AuthUser> {
    const credential = await createUserWithEmailAndPassword(this.auth, email, password);
    if (!credential.user.emailVerified) {
      await sendEmailVerification(credential.user);
    }
    return toAuthUser(credential.user)!;
  }

  sendPasswordResetEmail(email: string): Promise<void> {
    return firebaseSendPasswordResetEmail(this.auth, email);
  }

  verifyPasswordResetCode(code: string): Promise<string> {
    return firebaseVerifyPasswordResetCode(this.auth, code);
  }

  confirmPasswordReset(code: string, newPassword: string): Promise<void> {
    return firebaseConfirmPasswordReset(this.auth, code, newPassword);
  }

  signOut(): Promise<void> {
    return firebaseSignOut(this.auth);
  }

  async updateDisplayName(displayName: string): Promise<void> {
    const current = this.auth.currentUser;
    if (!current) throw new Error('No authenticated user');
    await updateProfile(current, { displayName });
  }

  onAuthStateChanged(callback: (user: AuthUser | null) => void): AuthUnsubscribe {
    return firebaseOnAuthStateChanged(this.auth, (user) => callback(toAuthUser(user)));
  }

  async reauthenticateGoogleAndDelete(): Promise<void> {
    const current = this.auth.currentUser;
    if (!current) throw new Error('No authenticated user');
    await reauthenticateWithPopup(current, new GoogleAuthProvider());
    await current.delete();
  }

  async reauthenticateEmailAndDelete(email: string, password: string): Promise<void> {
    const current = this.auth.currentUser;
    if (!current) throw new Error('No authenticated user');
    const credential = EmailAuthProvider.credential(email, password);
    await reauthenticateWithCredential(current, credential);
    await current.delete();
  }

  get currentUser(): AuthUser | null {
    return toAuthUser(this.auth.currentUser);
  }
}
